import {
  useCallback,
  useMemo,
  type CSSProperties,
} from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { adminFocusable, adminTokens } from '../../admin-ui';
import { useLanguageContext } from '../../hooks/useLanguageContext';
import { createSearchableContextPickerStyles } from './searchableContextPickerStyles';
import { useSearchableContextPicker } from './useSearchableContextPicker';

export function LanguageContextPicker() {
  const {
    selectedLocale,
    selectedLanguageLabel,
    setSelectedLocale,
    languageOptions,
    isLanguageCatalogLoading,
    isLanguageCatalogError,
  } = useLanguageContext();
  const {
    isOpen,
    searchQuery,
    setSearchQuery,
    triggerRef,
    containerRef,
    searchInputRef,
    closeDropdown,
    toggleDropdown,
    handleKeyDown,
  } = useSearchableContextPicker();
  const styles = useMemo(() => createStyles(), []);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLocaleLowerCase();
    if (!normalizedQuery) return languageOptions;
    return languageOptions.filter((option) =>
      option.label.toLocaleLowerCase().includes(normalizedQuery)
    );
  }, [languageOptions, searchQuery]);

  const selectLocale = useCallback((locale: string) => {
    setSelectedLocale(locale);
    closeDropdown();
  }, [closeDropdown, setSelectedLocale]);

  return (
    <div ref={containerRef} style={styles.container} onKeyDown={handleKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleDropdown}
        style={styles.trigger}
        aria-label={`Authoring language: ${selectedLanguageLabel}`}
        title={selectedLanguageLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span style={styles.triggerLabel}>{selectedLanguageLabel}</span>
        <ChevronDown size={14} strokeWidth={1.75} style={{ flexShrink: 0 }} aria-hidden="true" />
      </button>

      {isOpen && (
        <div style={styles.dropdown} role="dialog" aria-label="Select authoring language">
          <div style={styles.searchWrapper}>
            <span style={styles.searchIcon} aria-hidden="true">
              <Search size={14} strokeWidth={1.75} />
            </span>
            <input
              ref={searchInputRef}
              type="search"
              placeholder="Search languages…"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              style={styles.searchInput}
              aria-label="Search languages"
              autoComplete="off"
            />
          </div>

          <ul role="listbox" aria-label="Authoring language options" style={styles.list}>
            {filteredOptions.map((option) => {
              const isSelected = option.locale === selectedLocale;
              return (
                <li key={option.locale} style={styles.listEntry}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => selectLocale(option.locale)}
                    style={{
                      ...styles.listItem,
                      ...(isSelected ? styles.listItemSelected : {}),
                    }}
                  >
                    {option.label}
                  </button>
                </li>
              );
            })}
            {isLanguageCatalogLoading && (
              <li style={styles.statusItem} role="status">Loading languages…</li>
            )}
            {isLanguageCatalogError && !isLanguageCatalogLoading && (
              <li style={styles.statusItem} role="alert">
                Could not load supported languages. English (US) remains available.
              </li>
            )}
            {!isLanguageCatalogLoading && !isLanguageCatalogError && filteredOptions.length === 0 && (
              <li style={styles.statusItem}>No languages match your search.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function createStyles(): Record<string, CSSProperties> {
  return {
    ...createSearchableContextPickerStyles({
      triggerMinWidth: '170px',
      triggerMaxWidth: '250px',
      dropdownWidth: '300px',
      listMaxHeight: '300px',
    }),
    listEntry: {
      margin: 0,
      padding: 0,
    },
    listItem: {
      ...adminFocusable,
      outlineOffset: '-2px',
      width: '100%',
      display: 'block',
      padding: `${adminTokens.spacing.sm} ${adminTokens.spacing.md}`,
      border: 0,
      borderRadius: adminTokens.radius.control,
      backgroundColor: 'transparent',
      color: adminTokens.text.primary,
      fontSize: adminTokens.typography.body.size,
      fontFamily: adminTokens.typography.fontFamily,
      lineHeight: 1.4,
      textAlign: 'left',
      cursor: 'pointer',
    },
  };
}
